// Story 15.6 — the platform organization directory list read (Screen 23).
//
// Query key is ["admin-orgs", filters], the shipped `useAdminUsers` convention:
// the filter object is part of the key, so two filter states never share a cache
// entry, and every WRITE invalidates the ["admin-orgs"] PREFIX — a partial key
// match, which refetches every cached filter permutation at once instead of only
// the one the operator last used. `useCreateOrganization` (AC28) and
// `useUpdateOrganization` (AC25) both invalidate that prefix.

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { AdminOrgsFilters, AdminOrgsListData } from "@/hooks/queries/admin-orgs-types";

export const ADMIN_ORGS_QUERY_KEY = "admin-orgs";

/**
 * The server's own page size (`list_organizations/endpoint.py`, `limit=25`,
 * `le=100`), mirrored so a caller can do pagination arithmetic against `count`
 * without sending a `limit` it would then have to keep in sync. Rule 06
 * §Pagination is the authority; the `limit: int = 20` in Rule 07's illustrative
 * repository snippet is a teaching example and NOT this contract.
 */
export const ADMIN_ORGS_PAGE_SIZE = 25;

export function adminOrgsQueryKey(filters: AdminOrgsFilters) {
  return [ADMIN_ORGS_QUERY_KEY, filters] as const;
}

/**
 * Builds the prefix-relative request path. Every value is encoded by
 * URLSearchParams; an empty/undefined filter is OMITTED rather than sent blank,
 * so a cleared search box is indistinguishable from one that was never used.
 *
 * `page` is 1-indexed server-side (`ge=1`) and is omitted on page 1, so the
 * first page's path is the bare route. `limit` is omitted unless the caller
 * chose one, leaving the server's default of 25 authoritative.
 */
export function adminOrgsPath(filters: AdminOrgsFilters): string {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  if (filters.limit) params.set("limit", String(filters.limit));

  const qs = params.toString();
  return qs ? `/admin/orgs?${qs}` : "/admin/orgs";
}

export function useAdminOrgs(filters: AdminOrgsFilters) {
  return useQuery({
    queryKey: adminOrgsQueryKey(filters),
    queryFn: () => apiFetch<AdminOrgsListData>(adminOrgsPath(filters)),
    // A refusal (403 for a non-super_admin) is terminal for this read; retrying
    // only delays the inline Banner the screen renders instead.
    retry: false,
  });
}
